import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Plus, Search, Filter, Edit, Trash2, Eye, CheckCircle, XCircle, Calendar, User } from 'lucide-react';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import { PERSIAN_TEXTS } from '../../constants/persianTexts';
import newsService from '../../services/newsService';
import toast from 'react-hot-toast';

const NewsContainer = styled.div`
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  flex-wrap: wrap;
  gap: 20px;
`;

const Title = styled.h1`
  color: #ffffff;
  font-size: 28px;
  font-weight: 700;
  margin: 0;
  background: linear-gradient(135deg, #00d4ff 0%, #4ecdc4 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const SearchContainer = styled.div`
  display: flex;
  gap: 15px;
  align-items: center;
  flex-wrap: wrap;
`;

const FilterTabs = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  border: 1px solid #2d2d44;
  border-radius: 12px;
  padding: 5px;
`;

const FilterTab = styled.button`
  background: ${props => props.active ? 'linear-gradient(135deg, #00d4ff 0%, #4ecdc4 100%)' : 'transparent'};
  border: none;
  border-radius: 8px;
  padding: 10px 20px;
  color: ${props => props.active ? 'white' : '#b8b8d1'};
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 500;
  
  &:hover {
    background: ${props => props.active ? 'linear-gradient(135deg, #00d4ff 0%, #4ecdc4 100%)' : 'rgba(0, 212, 255, 0.1)'};
    color: ${props => props.active ? 'white' : '#00d4ff'};
  }
`;

const NewsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
`;

const NewsCard = styled(motion.div)`
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  border: 1px solid #2d2d44;
  border-radius: 12px;
  overflow: hidden;
  position: relative;
  cursor: pointer;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${props => {
      switch(props.status) {
        case 'verified': return 'linear-gradient(90deg, #4ecdc4 0%, #44a08d 100%)';
        case 'pending': return 'linear-gradient(90deg, #ffd93d 0%, #ff6b6b 100%)';
        default: return 'linear-gradient(90deg, #6c6c8a 0%, #4a4a6a 100%)';
      }
    }};
  }
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.4);
  }
`;

const NewsImage = styled.div`
  width: 100%;
  height: 200px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 48px;
  color: white;
  position: relative;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const NewsContent = styled.div`
  padding: 20px;
`;

const NewsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 15px;
`;

const NewsTitle = styled.h3`
  color: #ffffff;
  font-size: 18px;
  font-weight: 600;
  margin: 0;
  line-height: 1.4;
`;

const NewsStatus = styled.div`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  background: ${props => {
    switch(props.status) {
      case 'verified': return 'linear-gradient(135deg, #4ecdc4 0%, #44a08d 100%)';
      case 'pending': return 'linear-gradient(135deg, #ffd93d 0%, #ff6b6b 100%)';
      default: return 'linear-gradient(135deg, #6c6c8a 0%, #4a4a6a 100%)';
    }
  }};
  color: white;
`;

const NewsDescription = styled.p`
  color: #b8b8d1;
  font-size: 14px;
  line-height: 1.6;
  margin: 0 0 15px 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const NewsMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  font-size: 12px;
  color: #6c6c8a;
`;

const NewsActions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
`;

const ActionButton = styled.button`
  background: none;
  border: 1px solid #2d2d44;
  border-radius: 6px;
  padding: 8px;
  color: #b8b8d1;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: #2d2d44;
    color: #00d4ff;
    border-color: #00d4ff;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #6c6c8a;
`;

const EmptyIcon = styled.div`
  font-size: 64px;
  margin-bottom: 20px;
`;

const EmptyTitle = styled.h3`
  color: #b8b8d1;
  font-size: 20px;
  margin: 0 0 10px 0;
`;

const EmptyDescription = styled.p`
  color: #6c6c8a;
  font-size: 16px;
  margin: 0;
`;

const News = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      setLoading(true);
      const response = await newsService.getNews();
      const sortedNews = Array.isArray(response) 
        ? response.sort((a, b) => new Date(b.publishDate) - new Date(a.publishDate))
        : [];
      setNews(sortedNews);
    } catch (error) {
      toast.error('خطا در بارگذاری اخبار');
      console.error('Error fetching news:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredNews = news.filter(item => {
    const matchesSearch = item.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.content?.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (filter === 'all') return matchesSearch;
    if (filter === 'verified') return matchesSearch && item.isVerified;
    if (filter === 'pending') return matchesSearch && !item.isVerified;
    
    return matchesSearch;
  });

  const handleVerifyNews = async (newsId) => {
    try {
      setVerifying(true);
      await newsService.verifyNews(newsId);
      
      setNews(prevNews => 
        prevNews.map(item => 
          item.id === newsId 
            ? { ...item, isVerified: true }
            : item
        )
      );
      
      toast.success('خبر با موفقیت تایید شد');
    } catch (error) {
      toast.error('خطا در تایید خبر');
      console.error('Error verifying news:', error);
    } finally {
      setVerifying(false);
    }
  };

  const handleEditNews = (newsItem) => {
    // TODO: Implement edit news modal
    toast.success(`ویرایش خبر: ${newsItem.title}`);
  };

  const handleDeleteNews = (newsItem) => {
    if (window.confirm('آیا از حذف این خبر اطمینان دارید؟')) {
      // TODO: Implement delete news
      toast.success(`حذف خبر: ${newsItem.title}`);
    }
  };

  const handleViewNews = (newsItem) => {
    // TODO: Implement view news modal
    toast.success(`مشاهده خبر: ${newsItem.title}`);
  };

  const getStatusText = (isVerified) => {
    return isVerified ? 'تایید شده' : 'در انتظار';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fa-IR');
  };

  if (loading) {
    return (
      <NewsContainer>
        <div style={{ textAlign: 'center', padding: '60px' }}>
          <div style={{ color: '#b8b8d1' }}>در حال بارگذاری اخبار...</div>
        </div>
      </NewsContainer>
    );
  }

  return (
    <NewsContainer>
      <Header>
        <Title>مدیریت اخبار</Title>
        <SearchContainer>
          <Input
            placeholder="جستجو در اخبار..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ minWidth: '250px' }}
          />
          <Button variant="outline" icon={<Filter size={16} />}>
            فیلتر
          </Button>
          <Button variant="primary" icon={<Plus size={16} />}>
            افزودن خبر
          </Button>
        </SearchContainer>
      </Header>

      <FilterTabs>
        <FilterTab 
          active={filter === 'all'} 
          onClick={() => setFilter('all')}
        >
          همه اخبار
        </FilterTab>
        <FilterTab 
          active={filter === 'verified'} 
          onClick={() => setFilter('verified')}
        >
          تایید شده
        </FilterTab>
        <FilterTab 
          active={filter === 'pending'} 
          onClick={() => setFilter('pending')}
        >
          در انتظار
        </FilterTab>
      </FilterTabs>

      {filteredNews.length === 0 ? (
        <EmptyState>
          <EmptyIcon>📰</EmptyIcon>
          <EmptyTitle>هیچ خبری یافت نشد</EmptyTitle>
          <EmptyDescription>
            {searchTerm ? 'نتیجه‌ای برای جستجوی شما یافت نشد' : 'هنوز خبری ایجاد نشده است'}
          </EmptyDescription>
        </EmptyState>
      ) : (
        <NewsGrid>
          {filteredNews.map((newsItem, index) => (
            <NewsCard
              key={newsItem.id || index}
              status={newsItem.isVerified ? 'verified' : 'pending'}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
            >
              <NewsImage>
                {newsItem.imageUrl ? (
                  <img src={newsItem.imageUrl} alt={newsItem.title} />
                ) : (
                  '📰'
                )}
              </NewsImage>
              
              <NewsContent>
                <NewsHeader>
                  <NewsTitle>{newsItem.title || 'خبر بدون عنوان'}</NewsTitle>
                  <NewsStatus status={newsItem.isVerified ? 'verified' : 'pending'}>
                    {getStatusText(newsItem.isVerified)}
                  </NewsStatus>
                </NewsHeader>
                
                <NewsDescription>
                  {newsItem.content || 'توضیحاتی برای این خبر وجود ندارد'}
                </NewsDescription>
                
                <NewsMeta>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <Calendar size={14} />
                    {formatDate(newsItem.publishDate)}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <User size={14} />
                    {newsItem.sourceName || 'نامشخص'}
                  </div>
                </NewsMeta>
                
                <NewsActions>
                  <ActionButton onClick={() => handleViewNews(newsItem)} title="مشاهده">
                    <Eye size={16} />
                  </ActionButton>
                  {!newsItem.isVerified && (
                    <ActionButton 
                      onClick={() => handleVerifyNews(newsItem.id)} 
                      title="تایید"
                      disabled={verifying}
                    >
                      <CheckCircle size={16} />
                    </ActionButton>
                  )}
                  <ActionButton onClick={() => handleEditNews(newsItem)} title="ویرایش">
                    <Edit size={16} />
                  </ActionButton>
                  <ActionButton onClick={() => handleDeleteNews(newsItem)} title="حذف">
                    <Trash2 size={16} />
                  </ActionButton>
                </NewsActions>
              </NewsContent>
            </NewsCard>
          ))}
        </NewsGrid>
      )}
    </NewsContainer>
  );
};

export default News;
